import { interpolateZoom } from 'd3-interpolate';

/** Policies shared by Preview, screenplay playback, and offline video rendering. */
export type CameraTransitionPolicy =
  | 'AUTO'
  | 'ARC_FLYOVER'
  | 'DIRECT_PAN'
  | 'FIT_BOTH_FLYOVER'
  | 'FULL_PAGE_THEN_FOCUS'
  | 'INSTANT';

export interface CameraView {
  /** Page-space center. Preview uses normalized page coordinates; video uses source pixels. */
  centerX: number;
  centerY: number;
  /** Visible page-space width across the usable viewport. */
  visibleWidth: number;
  viewportAspectRatio: number;
  sourceIdentity?: string;
}

export interface CameraRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CameraTransitionRequest {
  fromView: CameraView;
  toView: CameraView;
  sourceGeometry?: CameraRect;
  targetGeometry: CameraRect;
  policy?: CameraTransitionPolicy;
  pageBounds?: CameraRect;
  explicitDurationMs?: number;
  curvature?: number;
  reducedMotion?: boolean;
}

export interface CameraTransitionPhase {
  kind: 'ARC' | 'DIRECT' | 'ZOOM' | 'HOLD';
  startMs: number;
  endMs: number;
  fromView: CameraView;
  toView: CameraView;
  /**
   * ARC only: the middle control point of the quadratic curve that the logarithm of the visible
   * width follows. It is chosen so the curve peaks at the arc's widest view (Issue #3126).
   */
  logWidthControl?: number;
}

export interface CameraTransitionPlan {
  requestedPolicy: CameraTransitionPolicy;
  selectedPolicy: Exclude<CameraTransitionPolicy, 'AUTO'>;
  reason: string;
  durationMs: number;
  contextView: CameraView;
  phases: CameraTransitionPhase[];
  curvature: number;
}

const DEFAULT_PAGE_BOUNDS: CameraRect = { x: 0, y: 0, width: 1, height: 1 };
const DEFAULT_CURVATURE = Math.SQRT2;
const DIRECT_SCALE_RATIO_LIMIT = 1.2;
const FIT_BOTH_FULL_PAGE_RATIO = 0.86;
const CONTEXT_MIN_WIDENING = 1.5;
const INTERACTIVE_CONTEXT_DWELL_MS = 180;
/** Padding around both regions in the widest view of an arc, as for the fit-both context view. */
const ARC_APEX_PADDING = 0.18;
/**
 * How much slower an arc flight runs than the suggested durations it starts from. The operator found
 * the first version too fast for following the move (Issue #3126, 2026-10-25).
 */
const ARC_DURATION_SCALE = 1.5;
const ARC_MIN_DURATION_MS = 360;
const ARC_REFIT_PASSES = 8;
const ARC_MAX_DURATION_MS = 1000;

type CameraZoomInterpolator = ((progress: number) => [number, number, number]) & {
  duration: number;
};
type CameraZoomFactory = ((from: [number, number, number], to: [number, number, number]) => CameraZoomInterpolator) & {
  rho: (curvature: number) => CameraZoomFactory;
};
const cameraInterpolateZoom = interpolateZoom as unknown as CameraZoomFactory;

/** Select a deterministic policy and build its time-addressable phases. */
export function planCameraTransition(request: CameraTransitionRequest): CameraTransitionPlan {
  validateView(request.fromView, 'fromView');
  validateView(request.toView, 'toView');
  const bounds = request.pageBounds ?? DEFAULT_PAGE_BOUNDS;
  validateRect(bounds, 'pageBounds');
  validateRect(request.targetGeometry, 'targetGeometry');
  if (request.sourceGeometry) validateRect(request.sourceGeometry, 'sourceGeometry');

  const requestedPolicy = request.policy ?? 'AUTO';
  const curvature = Math.max(0.001, request.curvature ?? DEFAULT_CURVATURE);
  const fullPage = fitRect(bounds, request.toView.viewportAspectRatio, 0.03, bounds, request.toView.sourceIdentity);
  const union = request.sourceGeometry
    ? unionRect(request.sourceGeometry, request.targetGeometry)
    : request.targetGeometry;
  const fitBoth = fitRect(union, request.toView.viewportAspectRatio, 0.18, bounds, request.toView.sourceIdentity);

  let selectedPolicy: Exclude<CameraTransitionPolicy, 'AUTO'>;
  let reason: string;
  if (request.reducedMotion || requestedPolicy === 'INSTANT') {
    selectedPolicy = 'INSTANT';
    reason = request.reducedMotion
      ? 'Reduced motion requests the equivalent final camera without travel.'
      : 'The caller requested an instant transition.';
  } else if (requestedPolicy !== 'AUTO') {
    selectedPolicy = requestedPolicy;
    reason = `The caller requested ${requestedPolicy}.`;
  } else if (
    request.fromView.sourceIdentity &&
    request.toView.sourceIdentity &&
    request.fromView.sourceIdentity !== request.toView.sourceIdentity
  ) {
    selectedPolicy = 'FULL_PAGE_THEN_FOCUS';
    reason = 'The source changed, so the destination page establishes context before focusing.';
  } else if (!request.sourceGeometry) {
    selectedPolicy = 'DIRECT_PAN';
    reason = 'No prior same-page region was supplied, so the initial focus uses a direct move.';
  } else if (
    containsRect(request.fromView, union, 0.08) &&
    scaleRatio(request.fromView, request.toView) <= DIRECT_SCALE_RATIO_LIMIT
  ) {
    selectedPolicy = 'DIRECT_PAN';
    reason = 'The current view already contains both regions and the target needs no material scale change.';
  } else if (
    contextCandidate(fitBoth, fullPage).visibleWidth <
    Math.max(request.fromView.visibleWidth, request.toView.visibleWidth) * CONTEXT_MIN_WIDENING
  ) {
    selectedPolicy = 'DIRECT_PAN';
    reason = 'A context view would barely widen beyond the wider endpoint, so it would only add a zoom out and back.';
  } else if (request.sourceGeometry && fitBoth.visibleWidth <= fullPage.visibleWidth * FIT_BOTH_FULL_PAGE_RATIO) {
    selectedPolicy = 'FIT_BOTH_FLYOVER';
    reason = 'Both regions fit together without reducing the view to whole-page context.';
  } else {
    selectedPolicy = 'FULL_PAGE_THEN_FOCUS';
    reason = 'Fitting both regions is effectively a whole-page view.';
  }

  if (selectedPolicy === 'INSTANT') {
    return {
      requestedPolicy,
      selectedPolicy,
      reason,
      durationMs: 0,
      contextView: request.toView,
      phases: [{ kind: 'HOLD', startMs: 0, endMs: 0, fromView: request.toView, toView: request.toView }],
      curvature,
    };
  }

  if (selectedPolicy === 'ARC_FLYOVER') {
    return planArc(request, requestedPolicy, reason, union, fullPage, curvature);
  }

  if (selectedPolicy === 'DIRECT_PAN') {
    const durationMs = resolvedDuration(request.explicitDurationMs, directDuration(request.fromView, request.toView));
    return {
      requestedPolicy,
      selectedPolicy,
      reason,
      durationMs,
      contextView: request.fromView,
      phases: [{ kind: 'DIRECT', startMs: 0, endMs: durationMs, fromView: request.fromView, toView: request.toView }],
      curvature,
    };
  }

  const contextView = selectedPolicy === 'FIT_BOTH_FLYOVER' ? fitBoth : fullPage;
  const startView =
    request.fromView.sourceIdentity &&
    request.toView.sourceIdentity &&
    request.fromView.sourceIdentity !== request.toView.sourceIdentity
      ? contextView
      : request.fromView;
  const firstWeight = zoomDuration(startView, contextView, curvature);
  const secondWeight = zoomDuration(contextView, request.toView, curvature);
  const dwellMs = selectedPolicy === 'FULL_PAGE_THEN_FOCUS' ? INTERACTIVE_CONTEXT_DWELL_MS : 0;
  const suggestedTravel = Math.max(420, Math.min(1100, (firstWeight + secondWeight) * 0.42));
  const travelMs = Math.max(1, resolvedDuration(request.explicitDurationMs, suggestedTravel) - dwellMs);
  const firstMs = firstWeight + secondWeight > 0 ? travelMs * (firstWeight / (firstWeight + secondWeight)) : 0;
  const phases: CameraTransitionPhase[] = [];
  let cursor = 0;
  if (firstMs >= 1) {
    phases.push({ kind: 'ZOOM', startMs: 0, endMs: firstMs, fromView: startView, toView: contextView });
    cursor = firstMs;
  }
  if (dwellMs > 0) {
    phases.push({ kind: 'HOLD', startMs: cursor, endMs: cursor + dwellMs, fromView: contextView, toView: contextView });
    cursor += dwellMs;
  }
  phases.push({
    kind: 'ZOOM',
    startMs: cursor,
    endMs: cursor + (travelMs - firstMs),
    fromView: contextView,
    toView: request.toView,
  });
  const durationMs = cursor + (travelMs - firstMs);

  return { requestedPolicy, selectedPolicy, reason, durationMs, contextView, phases, curvature };
}

/** Sample a planned transition at an elapsed time without retaining runtime state. */
export function sampleCameraTransition(plan: CameraTransitionPlan, elapsedMs: number): CameraView {
  if (plan.durationMs <= 0 || elapsedMs >= plan.durationMs) {
    return plan.phases[plan.phases.length - 1].toView;
  }
  if (elapsedMs <= 0) return plan.phases[0].fromView;
  const phase = plan.phases.find(candidate => elapsedMs <= candidate.endMs) ?? plan.phases[plan.phases.length - 1];
  if (phase.kind === 'HOLD' || phase.endMs <= phase.startMs) return phase.toView;
  const progress = (elapsedMs - phase.startMs) / (phase.endMs - phase.startMs);
  if (phase.kind === 'DIRECT') return directInterpolate(phase.fromView, phase.toView, progress);
  if (phase.kind === 'ARC') return arcInterpolate(phase, progress);
  const zoom = cameraInterpolateZoom.rho(plan.curvature)(
    [phase.fromView.centerX, phase.fromView.centerY, phase.fromView.visibleWidth],
    [phase.toView.centerX, phase.toView.centerY, phase.toView.visibleWidth]
  );
  const [centerX, centerY, visibleWidth] = zoom(Math.max(0, Math.min(1, progress)));
  return {
    centerX,
    centerY,
    visibleWidth,
    viewportAspectRatio: phase.toView.viewportAspectRatio,
    sourceIdentity: phase.toView.sourceIdentity ?? phase.fromView.sourceIdentity,
  };
}

/** Report whether a camera contains a rectangle plus semantic padding. */
export function viewContainsRect(view: CameraView, rect: CameraRect, paddingFraction = 0): boolean {
  return containsRect(view, rect, paddingFraction);
}

/** Return the context view AUTO would fly through: fit-both unless that is effectively the whole page. */
function contextCandidate(fitBoth: CameraView, fullPage: CameraView): CameraView {
  return fitBoth.visibleWidth <= fullPage.visibleWidth * FIT_BOTH_FULL_PAGE_RATIO ? fitBoth : fullPage;
}

/**
 * Plan one continuous move between two views of the same page (Issue #3126).
 *
 * The camera center travels along the straight line between the two views, and the visible width
 * rises and falls on the way: its widest view shows both regions, never more than the whole page,
 * and when that is no wider than the wider endpoint the width simply changes from one end to the
 * other. Position and width share one easing curve, so the move starts and lands at rest and never
 * stops in between. Interactive Preview moves use this; narrated videos keep their own policies.
 */
function planArc(
  request: CameraTransitionRequest,
  requestedPolicy: CameraTransitionPolicy,
  reason: string,
  union: CameraRect,
  fullPage: CameraView,
  curvature: number
): CameraTransitionPlan {
  const from = request.fromView;
  const to = request.toView;
  const startLog = Math.log(from.visibleWidth);
  const endLog = Math.log(to.visibleWidth);
  const widestLog = Math.log(fullPage.visibleWidth);
  const aspectRatio = to.viewportAspectRatio;

  // The widest view must show both regions from where the camera is at that moment. Estimate it
  // from the centered fit of both, then refit where on the line the peak falls. A wider peak moves
  // that point, so refit until the view there holds both regions or reaches the whole page; the
  // width only grows, and it settles within a few passes.
  let peakLog = Math.min(widestLog, Math.log(fitWidth(union, aspectRatio)));
  let control = arcControl(startLog, endLog, peakLog);
  if (control === null) {
    // No zoom-out looks necessary, but the wider end view is centered on its own region. If it does
    // not hold both, the arc must rise above it.
    const wider = from.visibleWidth >= to.visibleWidth ? from : to;
    const neededLog = Math.log(widthAround(wider, union, aspectRatio));
    if (neededLog > Math.log(wider.visibleWidth) + 1e-12) {
      peakLog = Math.min(widestLog, neededLog);
      control = arcControl(startLog, endLog, peakLog);
    }
  }
  for (let pass = 0; control !== null && pass < ARC_REFIT_PASSES && peakLog < widestLog; pass++) {
    const center = pointOnLine(from, to, arcPeakProgress(startLog, endLog, control));
    const neededLog = Math.log(widthAround(center, union, aspectRatio));
    if (neededLog <= peakLog + 1e-12) break;
    peakLog = Math.min(widestLog, neededLog);
    control = arcControl(startLog, endLog, peakLog);
  }
  const logWidthControl = control ?? (startLog + endLog) / 2;

  let contextView = from.visibleWidth >= to.visibleWidth ? from : to;
  let suggested = directDuration(from, to);
  if (control !== null) {
    const peakProgress = arcPeakProgress(startLog, endLog, control);
    contextView = {
      ...pointOnLine(from, to, peakProgress),
      visibleWidth: Math.exp(peakLog),
      viewportAspectRatio: aspectRatio,
      sourceIdentity: to.sourceIdentity ?? from.sourceIdentity,
    };
    const weight = zoomDuration(from, contextView, curvature) + zoomDuration(contextView, to, curvature);
    suggested = Math.max(ARC_MIN_DURATION_MS, Math.min(ARC_MAX_DURATION_MS, weight * 0.42));
  }
  const durationMs = resolvedDuration(request.explicitDurationMs, suggested * ARC_DURATION_SCALE);
  return {
    requestedPolicy,
    selectedPolicy: 'ARC_FLYOVER',
    reason,
    durationMs,
    contextView,
    phases: [{ kind: 'ARC', startMs: 0, endMs: durationMs, fromView: from, toView: to, logWidthControl }],
    curvature,
  };
}

/**
 * Return the middle control point that makes the quadratic curve from `start` to `end` peak at
 * `peak`, or null when the peak is no higher than an endpoint and the curve should stay straight.
 *
 * The curve is (1-s)^2 start + 2s(1-s) control + s^2 end. Its maximum is
 * (start end - control^2) / (start + end - 2 control); setting that to `peak` and solving gives
 * control = peak + sqrt((peak - start)(peak - end)).
 */
function arcControl(start: number, end: number, peak: number): number | null {
  if (peak <= Math.max(start, end) + 1e-9) return null;
  return peak + Math.sqrt((peak - start) * (peak - end));
}

/** Return the curve parameter at which the quadratic log-width curve peaks. */
function arcPeakProgress(start: number, end: number, control: number): number {
  return (control - start) / (2 * control - start - end);
}

/** Sample an arc phase: eased straight-line position and a quadratic curve in log width. */
function arcInterpolate(phase: CameraTransitionPhase, progress: number): CameraView {
  const s = smootherStep(progress);
  const startLog = Math.log(phase.fromView.visibleWidth);
  const endLog = Math.log(phase.toView.visibleWidth);
  const control = phase.logWidthControl ?? (startLog + endLog) / 2;
  const logWidth = (1 - s) * (1 - s) * startLog + 2 * s * (1 - s) * control + s * s * endLog;
  return {
    ...pointOnLine(phase.fromView, phase.toView, s),
    visibleWidth: Math.exp(logWidth),
    viewportAspectRatio: phase.toView.viewportAspectRatio,
    sourceIdentity: phase.toView.sourceIdentity ?? phase.fromView.sourceIdentity,
  };
}

/** Return the camera center a fraction of the way along the straight line between two views. */
function pointOnLine(from: CameraView, to: CameraView, fraction: number): { centerX: number; centerY: number } {
  return {
    centerX: from.centerX + (to.centerX - from.centerX) * fraction,
    centerY: from.centerY + (to.centerY - from.centerY) * fraction,
  };
}

/** Return the visible width of a centered view that shows a padded rectangle. */
function fitWidth(rect: CameraRect, aspectRatio: number): number {
  const width = rect.width * (1 + ARC_APEX_PADDING * 2);
  const height = rect.height * (1 + ARC_APEX_PADDING * 2);
  return Math.max(Number.EPSILON, width, height * aspectRatio);
}

/** Return the visible width a view centered on `center` needs to show a padded rectangle. */
function widthAround(center: { centerX: number; centerY: number }, rect: CameraRect, aspectRatio: number): number {
  const padX = rect.width * ARC_APEX_PADDING;
  const padY = rect.height * ARC_APEX_PADDING;
  const halfWidth = Math.max(center.centerX - (rect.x - padX), rect.x + rect.width + padX - center.centerX);
  const halfHeight = Math.max(center.centerY - (rect.y - padY), rect.y + rect.height + padY - center.centerY);
  return Math.max(Number.EPSILON, halfWidth * 2, halfHeight * 2 * aspectRatio);
}

/** Interpolate a nearby move without adding a context apex. */
function directInterpolate(from: CameraView, to: CameraView, progress: number): CameraView {
  const t = smootherStep(progress);
  return {
    centerX: from.centerX + (to.centerX - from.centerX) * t,
    centerY: from.centerY + (to.centerY - from.centerY) * t,
    visibleWidth: Math.exp(Math.log(from.visibleWidth) + (Math.log(to.visibleWidth) - Math.log(from.visibleWidth)) * t),
    viewportAspectRatio: to.viewportAspectRatio,
    sourceIdentity: to.sourceIdentity ?? from.sourceIdentity,
  };
}

/** Quintic easing with zero velocity and acceleration at both endpoints. */
function smootherStep(value: number): number {
  const t = Math.max(0, Math.min(1, value));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Return D3's recommended duration for one smooth-zoom leg. */
function zoomDuration(from: CameraView, to: CameraView, curvature: number): number {
  const interpolation = cameraInterpolateZoom.rho(curvature)(
    [from.centerX, from.centerY, from.visibleWidth],
    [to.centerX, to.centerY, to.visibleWidth]
  );
  return Math.max(0, Math.abs(interpolation.duration));
}

/** Derive a bounded interactive duration for a direct move. */
function directDuration(from: CameraView, to: CameraView): number {
  const distance = Math.hypot(to.centerX - from.centerX, to.centerY - from.centerY) / from.visibleWidth;
  const zoom = Math.abs(Math.log(to.visibleWidth / from.visibleWidth));
  return Math.max(180, Math.min(420, 210 + distance * 120 + zoom * 90));
}

/** Prefer an authored duration and otherwise round the planner suggestion. */
function resolvedDuration(explicitDurationMs: number | undefined, suggested: number): number {
  return Math.round(explicitDurationMs === undefined ? suggested : Math.max(0, explicitDurationMs));
}

/** Return the larger reciprocal visible-width ratio. */
function scaleRatio(a: CameraView, b: CameraView): number {
  return Math.max(a.visibleWidth / b.visibleWidth, b.visibleWidth / a.visibleWidth);
}

/** Return the smallest rectangle containing both inputs. */
function unionRect(a: CameraRect, b: CameraRect): CameraRect {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return {
    x,
    y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    height: Math.max(a.y + a.height, b.y + b.height) - y,
  };
}

/** Fit a padded rectangle to an aspect-ratio-constrained camera. */
function fitRect(
  rect: CameraRect,
  aspectRatio: number,
  paddingFraction: number,
  bounds: CameraRect,
  sourceIdentity?: string
): CameraView {
  const width = Math.max(Number.EPSILON, rect.width * (1 + paddingFraction * 2));
  const height = Math.max(Number.EPSILON, rect.height * (1 + paddingFraction * 2));
  const visibleWidth = Math.max(width, height * aspectRatio);
  return clampView(
    {
      centerX: rect.x + rect.width / 2,
      centerY: rect.y + rect.height / 2,
      visibleWidth,
      viewportAspectRatio: aspectRatio,
      sourceIdentity,
    },
    bounds
  );
}

/** Keep the camera center within page bounds whenever its view fits. */
function clampView(view: CameraView, bounds: CameraRect): CameraView {
  const halfWidth = view.visibleWidth / 2;
  const halfHeight = view.visibleWidth / view.viewportAspectRatio / 2;
  const centerX =
    halfWidth * 2 <= bounds.width
      ? Math.max(bounds.x + halfWidth, Math.min(bounds.x + bounds.width - halfWidth, view.centerX))
      : bounds.x + bounds.width / 2;
  const centerY =
    halfHeight * 2 <= bounds.height
      ? Math.max(bounds.y + halfHeight, Math.min(bounds.y + bounds.height - halfHeight, view.centerY))
      : bounds.y + bounds.height / 2;
  return { ...view, centerX, centerY };
}

/** Test rectangle containment in the camera's visible extent. */
function containsRect(view: CameraView, rect: CameraRect, paddingFraction: number): boolean {
  const padX = rect.width * paddingFraction;
  const padY = rect.height * paddingFraction;
  const halfWidth = view.visibleWidth / 2;
  const halfHeight = view.visibleWidth / view.viewportAspectRatio / 2;
  return (
    rect.x - padX >= view.centerX - halfWidth &&
    rect.x + rect.width + padX <= view.centerX + halfWidth &&
    rect.y - padY >= view.centerY - halfHeight &&
    rect.y + rect.height + padY <= view.centerY + halfHeight
  );
}

/** Reject invalid camera values at the planner boundary. */
function validateView(view: CameraView, name: string): void {
  if (
    !Number.isFinite(view.centerX) ||
    !Number.isFinite(view.centerY) ||
    view.visibleWidth <= 0 ||
    view.viewportAspectRatio <= 0
  ) {
    throw new Error(`${name} must contain finite centers and positive visible dimensions`);
  }
}

/** Reject invalid geometry at the planner boundary. */
function validateRect(rect: CameraRect, name: string): void {
  if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y) || rect.width <= 0 || rect.height <= 0) {
    throw new Error(`${name} must contain a finite origin and positive dimensions`);
  }
}
